import { auth0 } from "@/config/auth0";
import {
  SCANNER_TYPE,
  SCANNER_TYPES_FOR_API,
  type ScannerType,
} from "@/pages/scanner-control/config";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useAuthStore } from "./useAuthStore";
import { getScannersData } from "@/features/pipescanner/api";
import { mergeChanges } from "@/utils/objects";
import type { PipeScanners } from "@/types/pipeScanners";
import { showToastError } from "@/utils/errors";

const requests = new Map();

const INITIAL_PIPE_SCANNERS_STATE: PipeScanners = {
  [SCANNER_TYPE.RESULTS]: [],
  [SCANNER_TYPE.INFO_PENDING]: [],
  [SCANNER_TYPE.ANALYSIS_PENDING]: [],
};

export const usePipeScannersStore = defineStore("pipeScanners", () => {
  const authStore = useAuthStore();

  const pipeScanners = ref(structuredClone(INITIAL_PIPE_SCANNERS_STATE));

  const mergedPipeScanners = computed(() => {
    const scannersValues = Object.values(pipeScanners.value);
    let merged: typeof scannersValues = [];

    for (const item of scannersValues) {
      merged = merged.concat(item);
    }

    return merged;
  });

  function setPipeScanners(changes: Partial<PipeScanners>) {
    mergeChanges(pipeScanners.value, changes);
  }
  function setScanners({ key, scanners }) {
    pipeScanners.value[key] = scanners;
  }
  function updateScanner({ key, scannerId, updateForm }) {
    pipeScanners.value[key] = pipeScanners.value[key].map((scanner) => {
      if (scanner.id !== scannerId) {
        return scanner;
      }
      const updatedScanner = { ...scanner };
      const fields = Object.getOwnPropertyNames(updateForm);
      fields.forEach((field) => {
        if (updatedScanner.hasOwnProperty(field)) {
          updatedScanner[field] = updateForm[field];
        }
      });

      return updatedScanner;
    });
  }
  function resetScanners() {
    pipeScanners.value = structuredClone(INITIAL_PIPE_SCANNERS_STATE);
  }

  const isLoading = ref({
    [SCANNER_TYPE.RESULTS]: false,
    [SCANNER_TYPE.INFO_PENDING]: false,
    [SCANNER_TYPE.ANALYSIS_PENDING]: false,
  });

  function setIsLoading(changes: Partial<typeof isLoading.value>) {
    mergeChanges(isLoading.value, changes);
  }

  async function fetchScanners(scannerType: ScannerType) {
    try {
      setIsLoading({ [scannerType]: true });
      setPipeScanners({ [scannerType]: [] });

      const token = await auth0.getAccessTokenSilently();
      const controller = new AbortController();

      requests.set(scannerType, controller);

      const scanners = await getScannersData(
        SCANNER_TYPES_FOR_API[scannerType],
        token,
        authStore.selectedGroup,
        controller
      );

      setPipeScanners({ [scannerType]: scanners });
    } catch (error) {
      showToastError(error);
    } finally {
      setIsLoading({ [scannerType]: false });
    }
  }
  async function fetchResultsScanners() {
    await fetchScanners(SCANNER_TYPE.RESULTS);
  }
  async function fetchInfoScanners() {
    await fetchScanners(SCANNER_TYPE.INFO_PENDING);
  }
  async function fetchAnalysisPendingScanners() {
    await fetchScanners(SCANNER_TYPE.ANALYSIS_PENDING);
  }
  function cancelAllRequests() {
    for (const request of requests.values()) {
      request.abort();
    }
  }

  return {
    pipeScanners,
    isLoading,
    mergedPipeScanners,
    setPipeScanners,
    setScanners,
    updateScanner,
    resetScanners,
    setIsLoading,
    fetchResultsScanners,
    fetchInfoScanners,
    fetchAnalysisPendingScanners,
    cancelAllRequests,
  };
});
